import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../config/api';
import { FiBriefcase, FiDollarSign, FiExternalLink } from 'react-icons/fi';
import Modal from './Modal';
import LoadingButton from './LoadingButton';
import MoreMenu from './MoreMenu';
import '../pages/Services.css';
import { formatLocalDate } from '../utils/formatTimestamp';
import formatMoney from '../utils/formatMoney';
import { appAlert } from '../utils/appDialog';
import { toYmd } from '../utils/serviceDuration';

const CandidateServicesPanel = ({ candidateId, canManagePayments = false }) => {
  const queryClient = useQueryClient();
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({
    amount: '',
    currency: 'USD',
    payment_date: toYmd(new Date()) || '',
    payment_mode: '',
    reference: '',
    notes: '',
  });

  const { data: enrollments = [], isLoading } = useQuery(
    ['candidate-services', candidateId],
    () => api.get(`/services/candidates/${candidateId}/enrollments`).then((r) => r.data),
    { enabled: !!candidateId }
  );

  const paymentMutation = useMutation(
    ({ enrollmentId, payload }) =>
      api.post(`/services/enrollments/${enrollmentId}/payments`, payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['candidate-services', candidateId]);
        setPayFor(null);
        const inv = res?.data?.document?.number;
        const invErr = res?.data?.invoice_error;
        if (inv) {
          void appAlert(`Payment recorded. Invoice ${inv} created and linked in finance.`);
        } else if (invErr) {
          void appAlert(
            `Payment recorded in finance ledger, but invoice was not created: ${invErr}`
          );
        } else {
          void appAlert('Payment recorded in finance ledger.');
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  if (isLoading) return <div className="loading">Loading services…</div>;

  return (
    <section className="candidate-section candidate-details-card candidate-services-section">
      <div className="candidate-services-head section-header">
        <h2>
          <FiBriefcase /> Service packages
        </h2>
        <Link to="/services" className="btn btn-secondary btn-sm">
          <FiExternalLink /> Open Services
        </Link>
      </div>

      {enrollments.length === 0 ? (
        <p className="text-muted">No service packages enrolled yet.</p>
      ) : (
        <div className="svc-enrollment-list">
          {enrollments.map((e) => (
            <div key={e.id} className="svc-enrollment-row">
              <div>
                <strong>{e.package_name}</strong>
                <span>
                  {e.company_name}
                  {e.starts_at ? ` · start ${formatLocalDate(e.starts_at)}` : ''}
                  {e.ends_at ? ` · end ${formatLocalDate(e.ends_at)}` : ''}
                  {e.price_paid != null
                    ? ` · paid ${formatMoney(e.price_paid, e.currency)}`
                    : ''}
                </span>
              </div>
              <div className="svc-enrollment-row-actions">
                <span className={`svc-status svc-status--${e.status}`}>{e.status}</span>
                <MoreMenu
                  label={`Actions for ${e.package_name}`}
                  size="sm"
                  items={[
                    {
                      id: 'record-payment',
                      label: 'Record payment',
                      icon: FiDollarSign,
                      hidden: !canManagePayments,
                      onSelect: () => {
                        setPayFor(e);
                        setPayForm({
                          amount:
                            e.package_list_price != null
                              ? String(e.package_list_price)
                              : e.price_paid != null
                                ? String(e.price_paid)
                                : '',
                          currency: e.currency || e.package_currency || 'USD',
                          payment_date: toYmd(new Date()) || '',
                          payment_mode: '',
                          reference: '',
                          notes: '',
                        });
                      },
                    },
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!payFor} onClose={() => setPayFor(null)} ariaLabel="Record package payment">
        <h2>Record package payment</h2>
        {payFor ? (
          <>
            <p className="svc-form-hint">
              Payment for <strong>{payFor.package_name}</strong>. Finance will log package revenue and
              generate a sales invoice using the candidate&apos;s payment details.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                paymentMutation.mutate({
                  enrollmentId: payFor.id,
                  payload: {
                    amount: Number(payForm.amount),
                    currency: payForm.currency,
                    payment_date: payForm.payment_date,
                    payment_mode: payForm.payment_mode || undefined,
                    reference: payForm.reference || undefined,
                    notes: payForm.notes || undefined,
                    generate_invoice: true,
                  },
                });
              }}
            >
              <div className="svc-form-grid svc-form-grid--2">
                <div className="form-group">
                  <label>Amount</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={payForm.amount}
                    onChange={(ev) => setPayForm({ ...payForm, amount: ev.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <input
                    value={payForm.currency}
                    onChange={(ev) => setPayForm({ ...payForm, currency: ev.target.value.toUpperCase() })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Payment date</label>
                  <input
                    type="date"
                    value={payForm.payment_date}
                    onChange={(ev) => setPayForm({ ...payForm, payment_date: ev.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Payment mode</label>
                  <select
                    value={payForm.payment_mode}
                    onChange={(ev) => setPayForm({ ...payForm, payment_mode: ev.target.value })}
                  >
                    <option value="">— Optional (uses profile default) —</option>
                    <option value="bank">Bank transfer</option>
                    <option value="upi">UPI</option>
                    <option value="card">Card</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="paypal">PayPal</option>
                    <option value="wire">Wire</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Reference</label>
                  <input
                    value={payForm.reference}
                    onChange={(ev) => setPayForm({ ...payForm, reference: ev.target.value })}
                    placeholder="Txn id / receipt #"
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input
                    value={payForm.notes}
                    onChange={(ev) => setPayForm({ ...payForm, notes: ev.target.value })}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setPayFor(null)}>
                  Cancel
                </button>
                <LoadingButton
                  type="submit"
                  className="btn btn-primary"
                  loading={paymentMutation.isLoading}
                >
                  Record &amp; invoice
                </LoadingButton>
              </div>
            </form>
          </>
        ) : null}
      </Modal>
    </section>
  );
};

export default CandidateServicesPanel;
